import React, { useState, useEffect } from "react";
import { useOwner } from "../../../context/OwnerContext";

interface GeneralSettingsProps {
  onSave: (settings: any) => Promise<void>;
  isSaving: boolean;
}

const GeneralSettings: React.FC<GeneralSettingsProps> = ({
  onSave,
  isSaving,
}) => {
  const { owner } = useOwner();
  const [formData, setFormData] = useState({
    ownerName: "",
    masterEmail: "",
    contactPhone: "",
    timezone: "Europe/London",
    currencyFormat: "GBP",
    fiscalYearStart: "04-01", // MM-DD format
  });

  useEffect(() => {
    if (owner) {
      setFormData({
        ownerName: owner.name || "",
        masterEmail: owner.email || "",
        contactPhone: "",
        timezone: "Europe/London",
        currencyFormat: "GBP",
        fiscalYearStart: "04-01",
      });
    }
  }, [owner]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-md">
      <div className="px-4 py-5 sm:p-6">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Company Settings
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Owner account information and global preferences.
        </p>

        <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-3">
            <label
              htmlFor="ownerName"
              className="block text-sm font-medium text-gray-700"
            >
              Owner Name
            </label>
            <div className="mt-1">
              <input
                type="text"
                name="ownerName"
                id="ownerName"
                value={formData.ownerName}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="masterEmail"
              className="block text-sm font-medium text-gray-700"
            >
              Master Email
            </label>
            <div className="mt-1">
              <input
                type="email"
                name="masterEmail"
                id="masterEmail"
                value={formData.masterEmail}
                readOnly
                className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-xs text-gray-400">
              The master email is linked to your Clerk account and cannot be
              changed here.
            </p>
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="contactPhone"
              className="block text-sm font-medium text-gray-700"
            >
              Contact Phone
            </label>
            <div className="mt-1">
              <input
                type="tel"
                name="contactPhone"
                id="contactPhone"
                value={formData.contactPhone}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              />
            </div>
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="timezone"
              className="block text-sm font-medium text-gray-700"
            >
              Timezone
            </label>
            <div className="mt-1">
              <select
                id="timezone"
                name="timezone"
                value={formData.timezone}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="Europe/London">London (GMT/BST)</option>
                <option value="Europe/Paris">Paris (CET/CEST)</option>
                <option value="America/New_York">New York (EST/EDT)</option>
                <option value="America/Los_Angeles">
                  Los Angeles (PST/PDT)
                </option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
              </select>
            </div>
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="currencyFormat"
              className="block text-sm font-medium text-gray-700"
            >
              Currency
            </label>
            <div className="mt-1">
              <select
                id="currencyFormat"
                name="currencyFormat"
                value={formData.currencyFormat}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="GBP">British Pound (£)</option>
                <option value="EUR">Euro (€)</option>
                <option value="USD">US Dollar ($)</option>
              </select>
            </div>
          </div>

          <div className="sm:col-span-3">
            <label
              htmlFor="fiscalYearStart"
              className="block text-sm font-medium text-gray-700"
            >
              Fiscal Year Start
            </label>
            <div className="mt-1">
              <select
                id="fiscalYearStart"
                name="fiscalYearStart"
                value={formData.fiscalYearStart}
                onChange={handleInputChange}
                className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              >
                <option value="01-01">January 1st</option>
                <option value="04-01">April 1st</option>
                <option value="07-01">July 1st</option>
                <option value="10-01">October 1st</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
        <button
          type="submit"
          disabled={isSaving}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </form>
  );
};

export default GeneralSettings;
